import React, { useContext, useState, useEffect } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Typography, Alert } from '@one-thd/sui-atomic-components';
import {
  CustomerAccountNavigation,
  useAccountCrumbs,
} from '@thd-olt-component-react/customer-account-navigation';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import FAQs from '../common/FAQs';
import ProgramSectionContainer from './ProgramSection/ProgramSectionContainer';
import { getSvocId, getUserId } from '../../utils/utils';
import { ROUTES } from '../../constants/constants';
import { Verification } from './Verification';
import { getEnrollmentStatus } from '../../api/api';
import { mapCrumbsToStaticCrumbs } from '../../utils/breadcrumbs';
import { track } from '../../analytics/analytics';
import { PAGE_LOAD, GET_STARTED_LINK } from '../../constants/analytics';
import { GENERAL_ERROR_MSG, REDIRECT_ERROR_MSG } from '../../constants/alert-messages';
import { SectionSkeleton } from '../common/Loaders/Loaders';

const crumbs = [
  {
    label: ROUTES.landing.label,
    path: ROUTES.landing.url,
  },
];

export const LandingPage = () => {
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const [showIframe, setShowIframe] = useState(false);
  const header = showIframe ? 'Verification' : 'Military Discount Benefit';
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const staticCrumbs = mapCrumbsToStaticCrumbs(useAccountCrumbs(crumbs));
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isSpouse, setIsSpouse] = useState(false);

  const fetchCustomerEnrollment = async () => {
    setIsLoading(true);
    if (!getUserId() || !getSvocId()) {
      setApiError({ showRedirectAlert: true });
      window.location.replace('/auth/view/signin?redirect=/military/discount');
      return;
    }
    // eslint-disable-next-line consistent-return
    return getEnrollmentStatus(channel).then(function (resData) {
      const response = resData?.response;
      const error = resData?.error;

      if (response) {
        setApiResponse(response?.data?.militaryDiscount);
        setApiError(false);
        setIsLoading(false);
        if (
          response?.data?.militaryDiscount?.verificationId
          && response?.data?.militaryDiscount?.enrollmentStatus === 'N'
        ) {
          setShowIframe(true);
        }
      }
      setIsEnrolled(response?.data?.militaryDiscount?.enrollmentStatus === 'Y');
      setIsLoading(false);
      if (
        error?.response?.status === 401
        || (error?.response?.status === 400
        && error?.response?.data?.errors[0]?.errorCode === 'AUTH_ERR_101')
        || error?.response?.status === 403
      ) {
        setApiError({ showRedirectAlert: true });
        window.location.replace('/auth/view/signin?redirect=/military/discount');
      } else if (error) {
        setApiError(error);
        throw error;
      }

      return resData;
    });
  };

  useEffect(() => {
    fetchCustomerEnrollment()
      .then((resData) => {
        const response = resData?.response;
        if (response) {
          track(PAGE_LOAD, {
            isEnrolled: response?.data?.militaryDiscount?.enrollmentStatus === 'Y',
            hasMaxDiscount: response?.data?.militaryDiscount?.isMilitaryDiscountCapped,
          });
        }
      })
      .catch((error) => {
        if (error && !apiError?.showRedirectAlert) {
          setApiError({
            generalError: true,
          });
        }
      });
  }, []);

  /* This function will call the fetchCustomerEnrollment service to make
  /* sure user's session is still valid. If the session has expired, function in Axios Client
  /* will redirect user to Sign In page. If session is valid but APi returns any error, we will ignore it
  /* because that is irrelevant for the registration process. */

  const verifyOnClick = async (spouseFlag) => {
    track(GET_STARTED_LINK);
    setIsSpouse(spouseFlag);
    await fetchCustomerEnrollment().then(function () {
      setShowIframe(true);
    });
  };

  const pageContent = showIframe ? (
    <Verification apiResponse={apiResponse} isSpouse={isSpouse} />
  ) : (
    <>
      <div className="sui-grid sui-gap-3 sui-py-3">
        {!isConsumerApp && (
          <ErrorBoundary name="breadcrumbs">
            <QueryProvider cacheKey="military-discount-benefit-page">
              <Breadcrumbs staticCrumbs={staticCrumbs} />
            </QueryProvider>
          </ErrorBoundary>
        )}
        {!isMobile && (
          <Typography data-testid="page-header" weight="display" variant="h1" uppercase>
            {header}
          </Typography>
        )}
      </div>
      <div className="sui-grid sui-grid-cols-12 sui-gap-5 sui-py-3">
        <div className="sui-col-span-12 lg:sui-col-span-3" data-testid="left-nav">
          <ErrorBoundary name="customer-account-navigation">
            <QueryProvider cacheKey="left-nav">
              <CustomerAccountNavigation />
            </QueryProvider>
          </ErrorBoundary>
        </div>

        <div className="sui-col-span-12 lg:sui-col-span-9">
          {apiError?.generalError ? (
            <Alert status="error">{GENERAL_ERROR_MSG}</Alert>
          ) : (
            <>
              <div className="sui-mb-4">
                <Typography variant="h2">Our Thanks for Your Service</Typography>
                <Typography>
                  Receive 10% off eligible purchases* - every day, year round - if you&apos;re an
                  active service member, veteran or military spouse.
                </Typography>
              </div>
              {isLoading && <SectionSkeleton section="program" />}
              <ErrorBoundary name="program-section-container">
                <ProgramSectionContainer
                  isMobile={isMobile}
                  apiResponse={apiResponse}
                  apiError={apiError}
                  verifyOnClick={verifyOnClick}
                />
              </ErrorBoundary>
              <div className="sui-mb-4 sui-mt-16">
                <Typography variant="h2">FAQs</Typography>
              </div>
              {isLoading && <SectionSkeleton section="FAQ" />}
              <ErrorBoundary name="faqs">
                <FAQs apiResponse={apiResponse} />
              </ErrorBoundary>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="container">
      <div className="grid isBound">
        <div className="col__12-12 u--fix-960">
          {apiError?.showRedirectAlert ? (
            <div className="sui-mt-8">
              <Alert status="info">{REDIRECT_ERROR_MSG}</Alert>
            </div>
          ) : (
            pageContent
          )}
        </div>
      </div>
    </div>
  );
};

LandingPage.propTypes = {};
LandingPage.defaultProps = {};
LandingPage.displayName = 'LandingPage';
